/* ==========================================================================
   Kopstorm - Anavrin bundle builder

   The page holds every kit as a card with its variants written into data
   attributes, so picking, pricing and the ladder are pure arithmetic in the
   browser with no fetch until the shopper adds the set.

   The add is ONE /cart/add.js with every line carrying _kop_bundle, so a
   Shopify Function or a discount code can find the set later, and the drawer
   is opened with the same handover the rest of the Kopstorm work on this
   store uses.

   Money is never reconstructed by hand: the format string comes from a
   rendered sample in the markup, so multi currency and per market formats
   stay correct.
   ========================================================================== */

(function () {
  'use strict';

  var doc = document;

  function all(root, selector) {
    return Array.prototype.slice.call((root || doc).querySelectorAll(selector));
  }

  function one(root, selector) {
    return (root || doc).querySelector(selector);
  }

  function routes() {
    return (window.theme && window.theme.routes) || {};
  }

  /* ------------------------------------------------------------------------
     Money

     The section prints a known amount through the money filter. Reading the
     digits back out of it gives the separators, the symbol and its position
     without hardcoding anything.
     ------------------------------------------------------------------------ */

  function moneyFormatter(sample, cents) {
    var text = String(sample || '');
    var match = text.match(/[\d.,\u00a0\u202f ]*\d/);
    if (!match) {
      return function (value) {
        return (value / 100).toFixed(2);
      };
    }

    var number = match[0];
    var start = text.indexOf(number);
    var before = text.slice(0, start);
    var after = text.slice(start + number.length);

    var decimals = 2;
    var decimalSep = '.';
    var groupSep = ',';

    var tail = number.slice(-3);
    if (/^[.,]\d\d$/.test(tail)) {
      decimalSep = tail.charAt(0);
      groupSep = decimalSep === ',' ? '.' : ',';
    } else {
      decimals = 0;
    }

    /* A thousands separator that is neither a dot nor a comma (a space in some
       markets) is read straight off the sample. */
    var whole = decimals ? number.slice(0, -3) : number;
    var oddSep = whole.match(/[^\d]/);
    if (oddSep) groupSep = oddSep[0];

    return function (value) {
      var amount = Math.round(value) / 100;
      var negative = amount < 0;
      amount = Math.abs(amount);
      var fixed = amount.toFixed(decimals);
      var parts = fixed.split('.');
      var int = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, groupSep);
      var out = decimals ? int + decimalSep + parts[1] : int;
      return (negative ? '-' : '') + before + out + after;
    };
  }

  /* "245 Pieces" and "1,240 pieces" both give 245 and 1240. The facts come
     from a metaobject the client fills in by hand, so anything unparseable is
     worth nothing rather than worth guessing. */
  function countOf(raw) {
    if (!raw) return 0;
    var digits = String(raw).replace(/[^0-9]/g, '');
    return digits ? parseInt(digits, 10) : 0;
  }

  /* "8-10 Hours" is read as 10, so the estimate is the honest upper end of
     the range rather than a number the shopper beats and then doubts. */
  function hoursOf(raw) {
    if (!raw) return 0;
    var numbers = String(raw).match(/\d+(\.\d+)?/g);
    if (!numbers) return 0;
    return numbers.reduce(function (top, value) {
      return Math.max(top, parseFloat(value));
    }, 0);
  }

  function group(value) {
    return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function icon(name) {
    if (name === 'plus') {
      return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>';
    }
    if (name === 'minus') {
      return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12"></line></svg>';
    }
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>';
  }

  /* ------------------------------------------------------------------------
     Builder
     ------------------------------------------------------------------------ */

  function Builder(root) {
    this.root = root;
    this.money = moneyFormatter(root.getAttribute('data-kbb-money-sample'));
    this.mode = root.getAttribute('data-kbb-mode') || 'tiers';
    this.max = parseInt(root.getAttribute('data-kbb-max'), 10) || 12;
    this.tiers = this.readTiers(root.getAttribute('data-kbb-tiers'));
    this.threshold = this.shipThreshold();
    this.picked = [];
    this.adding = false;

    this.slots = one(root, '[data-kbb-slots]');
    this.empty = one(root, '[data-kbb-empty]');
    this.cta = all(root, '[data-kbb-add-all]');
    this.dock = one(root, '[data-kbb-dock]');

    this.bind();
    this.paint();
  }

  /* "2:10,3:15,4:20" - units, then percent off. In ladder mode the percent
     applies to the nth item onwards instead of the whole set. */
  Builder.prototype.readTiers = function (raw) {
    /* Normalised before parsing: on a multi language store any value that
       reaches the storefront through a translatable field can come back
       re-punctuated per locale. */
    return String(raw || '')
      .replace(/[^0-9:.,]/g, '')
      .split(',')
      .map(function (pair) {
        var bits = pair.split(':');
        return { units: parseInt(bits[0], 10), percent: parseFloat(bits[1]) };
      })
      .filter(function (tier) {
        return tier.units > 0 && !isNaN(tier.percent);
      })
      .sort(function (a, b) {
        return a.units - b.units;
      });
  };

  Builder.prototype.shipThreshold = function () {
    var base = parseFloat(this.root.getAttribute('data-kbb-ship')) || 0;
    if (!base) return 0;
    var rate = window.Shopify && window.Shopify.currency ? parseFloat(window.Shopify.currency.rate) : 1;
    if (!rate || isNaN(rate)) rate = 1;
    return Math.round(base * 100 * rate);
  };

  Builder.prototype.bind = function () {
    var self = this;

    this.root.addEventListener('click', function (event) {
      var tab = event.target.closest('[data-kbb-tab]');
      if (tab) {
        self.showTab(tab.getAttribute('data-kbb-tab'));
        return;
      }

      var add = event.target.closest('[data-kbb-add]');
      if (add) {
        event.preventDefault();
        self.change(add.closest('[data-kbb-card]'), 1);
        return;
      }

      var plus = event.target.closest('[data-kbb-plus]');
      if (plus) {
        event.preventDefault();
        self.change(plus.closest('[data-kbb-card]'), 1);
        return;
      }

      var minus = event.target.closest('[data-kbb-minus]');
      if (minus) {
        event.preventDefault();
        self.change(minus.closest('[data-kbb-card]'), -1);
        return;
      }

      var remove = event.target.closest('[data-kbb-remove]');
      if (remove) {
        event.preventDefault();
        self.removeId(remove.getAttribute('data-kbb-remove'));
        return;
      }

      var level = event.target.closest('[data-kbb-level-filter]');
      if (level) {
        event.preventDefault();
        self.filterLevel(level.getAttribute('data-kbb-level-filter'));
        return;
      }

      var toggle = event.target.closest('[data-kbb-sheet]');
      if (toggle) {
        event.preventDefault();
        self.toggleSheet();
        return;
      }

      var scrim = event.target.closest('[data-kbb-scrim]');
      if (scrim) {
        event.preventDefault();
        self.toggleSheet(false);
        return;
      }

      var submit = event.target.closest('[data-kbb-add-all]');
      if (submit) {
        event.preventDefault();
        if (submit.getAttribute('aria-disabled') === 'true') return;
        self.submit();
      }
    });

    this.root.addEventListener('change', function (event) {
      var select = event.target.closest('[data-kbb-variant]');
      if (!select) return;
      var card = select.closest('[data-kbb-card]');
      if (!card) return;
      /* A different variant is a different line. Move whatever was picked on
         the old one across so the choice never silently drops the kit. */
      var previous = card.getAttribute('data-kbb-current');
      var quantity = self.quantityOf(previous);
      if (quantity) {
        self.removeId(previous, true);
        card.setAttribute('data-kbb-current', select.value);
        self.change(card, quantity);
      } else {
        card.setAttribute('data-kbb-current', select.value);
        self.paintCard(card);
      }
    });
  };

  /* Filtering hides cards, it never removes them, so a kit already picked
     stays in the set and keeps its price while it is filtered out of view. */
  Builder.prototype.filterLevel = function (level) {
    all(this.root, '[data-kbb-level-filter]').forEach(function (button) {
      button.setAttribute('aria-pressed', button.getAttribute('data-kbb-level-filter') === level ? 'true' : 'false');
    });
    all(this.root, '[data-kbb-card]').forEach(function (card) {
      card.hidden = level !== '' && card.getAttribute('data-kbb-level') !== level;
    });
    all(this.root, '[data-kbb-promo]').forEach(function (card) {
      card.hidden = level !== '';
    });
  };

  /* The summary is a sheet on mobile and a sticky column on desktop, so the
     open state lives on both the panel and its scrim and nothing else moves. */
  Builder.prototype.toggleSheet = function (force) {
    var panel = one(this.root, '[data-kbb-summary]');
    var scrim = one(this.root, '[data-kbb-scrim]');
    if (!panel) return;
    var open = force === undefined ? !panel.classList.contains('is-open') : !!force;
    panel.classList.toggle('is-open', open);
    if (scrim) scrim.classList.toggle('is-open', open);
    doc.documentElement.classList.toggle('kbb-sheet-open', open);
  };

  Builder.prototype.showTab = function (id) {
    all(this.root, '[data-kbb-tab]').forEach(function (tab) {
      tab.setAttribute('aria-selected', tab.getAttribute('data-kbb-tab') === id ? 'true' : 'false');
    });
    all(this.root, '[data-kbb-panel]').forEach(function (panel) {
      panel.classList.toggle('is-on', panel.getAttribute('data-kbb-panel') === id);
    });
  };

  Builder.prototype.currentId = function (card) {
    return card ? card.getAttribute('data-kbb-current') : null;
  };

  Builder.prototype.quantityOf = function (id) {
    var line = this.find(id);
    return line ? line.quantity : 0;
  };

  Builder.prototype.find = function (id) {
    for (var i = 0; i < this.picked.length; i++) {
      if (this.picked[i].id === String(id)) return this.picked[i];
    }
    return null;
  };

  Builder.prototype.units = function () {
    return this.picked.reduce(function (sum, line) {
      return sum + line.quantity;
    }, 0);
  };

  Builder.prototype.change = function (card, delta) {
    if (!card) return;
    var id = this.currentId(card);
    if (!id) return;

    var line = this.find(id);
    var units = this.units();

    if (delta > 0 && units + delta > this.max) {
      delta = this.max - units;
      if (delta <= 0) return;
    }

    if (!line) {
      if (delta <= 0) return;
      var option = card.querySelector('[data-kbb-option="' + id + '"]');
      this.picked.push({
        id: String(id),
        quantity: delta,
        price: parseInt(card.getAttribute('data-kbb-price-' + id) || (option ? option.getAttribute('data-price') : 0), 10) || 0,
        title: card.getAttribute('data-kbb-title') || '',
        variant: option ? option.getAttribute('data-label') || '' : '',
        image: card.getAttribute('data-kbb-image') || '',
        url: card.getAttribute('data-kbb-url') || '',
        pieces: countOf(card.getAttribute('data-kbb-pieces')),
        hours: hoursOf(card.getAttribute('data-kbb-hours')),
        gift: card.getAttribute('data-kbb-gift') === '1'
      });
    } else {
      line.quantity += delta;
      if (line.quantity <= 0) {
        this.picked = this.picked.filter(function (item) {
          return item !== line;
        });
      }
    }

    this.paint();
  };

  Builder.prototype.removeId = function (id, quiet) {
    this.picked = this.picked.filter(function (line) {
      return line.id !== String(id);
    });
    if (!quiet) this.paint();
  };

  /* ------------------------------------------------------------------------
     Pricing
     ------------------------------------------------------------------------ */

  Builder.prototype.tierFor = function (units) {
    var found = null;
    this.tiers.forEach(function (tier) {
      if (units >= tier.units) found = tier;
    });
    return found;
  };

  Builder.prototype.nextTier = function (units) {
    for (var i = 0; i < this.tiers.length; i++) {
      if (this.tiers[i].units > units) return this.tiers[i];
    }
    return null;
  };

  Builder.prototype.totals = function () {
    var units = this.units();
    var subtotal = this.picked.reduce(function (sum, line) {
      return sum + line.price * line.quantity;
    }, 0);

    var discount = 0;

    if (this.mode === 'ladder') {
      /* Every unit expanded and sorted cheapest first, so the discount lands
         on the cheaper kits and the headline kit keeps its price. */
      var prices = [];
      this.picked.forEach(function (line) {
        for (var i = 0; i < line.quantity; i++) prices.push(line.price);
      });
      prices.sort(function (a, b) {
        return a - b;
      });
      var self = this;
      prices.forEach(function (price, index) {
        var position = prices.length - index; /* 1 is the most expensive */
        var tier = self.tierFor(position);
        if (tier) discount += Math.round((price * tier.percent) / 100);
      });
    } else {
      /* Worked out per line and rounded down, the same way Shopify's
         automatic product discount allocates it, so the page total always
         matches the cart and checkout to the cent. */
      var whole = this.tierFor(units);
      if (whole) {
        discount = this.picked.reduce(function (sum, line) {
          return sum + Math.floor((line.price * line.quantity * whole.percent) / 100);
        }, 0);
      }
    }

    var pieces = 0;
    var hours = 0;
    var gifts = 0;
    this.picked.forEach(function (line) {
      pieces += (line.pieces || 0) * line.quantity;
      hours += (line.hours || 0) * line.quantity;
      if (line.gift) gifts += line.quantity;
    });

    return {
      units: units,
      subtotal: subtotal,
      discount: discount,
      total: Math.max(0, subtotal - discount),
      pieces: pieces,
      hours: hours,
      gifts: gifts
    };
  };

  /* ------------------------------------------------------------------------
     Paint
     ------------------------------------------------------------------------ */

  Builder.prototype.paint = function () {
    var self = this;
    var sums = this.totals();

    all(this.root, '[data-kbb-card]').forEach(function (card) {
      self.paintCard(card);
    });

    this.root.setAttribute('data-kbb-picked', String(sums.units));

    if (this.slots) {
      this.slots.innerHTML = this.picked
        .map(function (line) {
          return (
            '<li class="kbb__slot">' +
            (line.image ? '<img src="' + line.image + '" alt="" width="46" height="46" loading="lazy">' : '<img src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7" alt="" width="46" height="46">') +
            '<span class="kbb__slot-text">' +
            '<span class="kbb__slot-name">' +
            line.title +
            '</span>' +
            '<span class="kbb__slot-sub">' +
            (line.variant ? line.variant + ' - ' : '') +
            line.quantity +
            ' x ' +
            self.money(line.price) +
            '</span>' +
            '</span>' +
            '<button type="button" class="kbb__slot-remove" data-kbb-remove="' +
            line.id +
            '" aria-label="Remove">' +
            icon('close') +
            '</button>' +
            '</li>'
          );
        })
        .join('');
    }

    if (this.empty) this.empty.hidden = sums.units > 0;

    this.text('[data-kbb-count]', sums.units + '/' + this.max);
    this.text('[data-kbb-subtotal]', this.money(sums.subtotal));
    this.text('[data-kbb-discount]', '-' + this.money(sums.discount));
    this.text('[data-kbb-total]', this.money(sums.total));

    all(this.root, '[data-kbb-save-row]').forEach(function (row) {
      row.hidden = sums.discount <= 0;
    });

    this.paintOffer(sums);
    this.paintShip(sums);
    this.paintBuild(sums);

    var label = this.root.getAttribute('data-kbb-cta-label') || 'Add set to cart';
    this.cta.forEach(function (button) {
      var minimum = parseInt(self.root.getAttribute('data-kbb-min'), 10) || 1;
      var ready = sums.units >= minimum;
      button.setAttribute('aria-disabled', ready ? 'false' : 'true');
      var text = one(button, '[data-kbb-cta-text]');
      if (text) {
        text.textContent = ready ? label + ' - ' + self.money(sums.total) : self.root.getAttribute('data-kbb-cta-empty') || label;
      }
    });

    if (this.dock) {
      this.dock.classList.toggle('is-on', sums.units > 0);
      var height = sums.units > 0 ? this.dock.offsetHeight : 0;
      this.root.style.setProperty('--kop-bb-bar-h', height + 'px');
      doc.documentElement.style.setProperty('--kop-bb-bar-h', height + 'px');
    }
  };

  Builder.prototype.paintCard = function (card) {
    var id = this.currentId(card);
    var quantity = this.quantityOf(id);
    card.classList.toggle('is-picked', quantity > 0);
    var value = one(card, '[data-kbb-value]');
    if (value) value.textContent = String(quantity);
    var count = one(card, '[data-kbb-card-count]');
    if (count) count.textContent = String(quantity);
  };

  Builder.prototype.text = function (selector, value) {
    all(this.root, selector).forEach(function (node) {
      node.textContent = value;
    });
  };

  Builder.prototype.paintOffer = function (sums) {
    var line = one(this.root, '[data-kbb-offer]');
    var meter = one(this.root, '[data-kbb-meter]');
    if (!line) return;

    var next = this.nextTier(sums.units);
    var current = this.tierFor(sums.units);

    var template;
    if (next) {
      template = this.root.getAttribute('data-kbb-offer-next') || 'Add [n] more to save [percent]%';
      line.textContent = template
        .replace('[n]', String(next.units - sums.units))
        .replace('[percent]', String(next.percent));
    } else if (current) {
      template = this.root.getAttribute('data-kbb-offer-max') || 'Best price unlocked: [percent]% off';
      line.textContent = template.replace('[percent]', String(current.percent));
    } else {
      line.textContent = this.root.getAttribute('data-kbb-offer-start') || '';
    }

    if (meter) {
      var top = this.tiers.length ? this.tiers[this.tiers.length - 1].units : 0;
      var ratio = top ? Math.min(1, sums.units / top) : 0;
      meter.style.width = Math.round(ratio * 100) + '%';
    }
  };

  /* The set as a build rather than as a basket: how many pieces are in the box
     and roughly how long the shelf takes to fill. Each half hides itself when
     the kits carry no facts, so a half filled metaobject never prints a zero. */
  Builder.prototype.paintBuild = function (sums) {
    var line = one(this.root, '[data-kbb-build]');
    if (line) {
      var template = this.root.getAttribute('data-kbb-build-line') || '';
      if (!template || !sums.units || (!sums.pieces && !sums.hours)) {
        line.hidden = true;
      } else {
        line.hidden = false;
        line.textContent = template
          .replace('[kits]', String(sums.units))
          .replace('[pieces]', group(sums.pieces))
          .replace('[hours]', String(Math.round(sums.hours)));
      }
    }

    var gifts = one(this.root, '[data-kbb-gifts]');
    if (gifts) {
      var giftTemplate = this.root.getAttribute('data-kbb-gift-line') || '';
      if (!giftTemplate || !sums.gifts) {
        gifts.hidden = true;
      } else {
        gifts.hidden = false;
        gifts.textContent = giftTemplate.replace('[n]', String(sums.gifts));
      }
    }
  };

  Builder.prototype.paintShip = function (sums) {
    var node = one(this.root, '[data-kbb-ship-line]');
    if (!node) return;
    if (!this.threshold) {
      node.hidden = true;
      return;
    }
    node.hidden = false;
    if (sums.total >= this.threshold) {
      node.textContent = this.root.getAttribute('data-kbb-ship-done') || '';
    } else {
      var left = this.threshold - sums.total;
      node.textContent = (this.root.getAttribute('data-kbb-ship-left') || '[amount] away from free shipping').replace(
        '[amount]',
        this.money(left)
      );
    }
  };

  /* ------------------------------------------------------------------------
     Add to cart
     ------------------------------------------------------------------------ */

  Builder.prototype.submit = function () {
    if (this.adding) return;
    var sums = this.totals();
    if (!sums.units) return;

    this.adding = true;
    this.cta.forEach(function (button) {
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('aria-disabled', 'true');
    });

    var self = this;
    var tag = String(this.root.getAttribute('data-kbb-tag') || 'street')
      .toLowerCase()
      .replace(/[^a-z0-9_-]/g, '') || 'street';
    var items = this.picked.map(function (line) {
      return {
        id: parseInt(line.id, 10),
        quantity: line.quantity,
        properties: { _kop_bundle: tag, _kop_bundle_size: String(sums.units) }
      };
    });

    window
      .fetch(routes().cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items: items })
      })
      .then(function (response) {
        return response.json();
      })
      .then(function (state) {
        if (state && state.status) throw new Error(state.description || state.message || 'cart error');
        return self.applyDiscount(sums);
      })
      .then(function () {
        return window.fetch(routes().cart_url || '/cart.js', { headers: { Accept: 'application/json' } });
      })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        if (window.theme && theme.pubsub && theme.pubsub.PUB_SUB_EVENTS) {
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'kopstorm-bundle-builder', cart: cart });
        }
        doc.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: cart } }));
        self.toggleSheet(false);
        self.openCart();
        self.picked = [];
        self.paint();
      })
      .catch(function (error) {
        console.error('Bundle builder add failed', error);
        var message = one(self.root, '[data-kbb-error]');
        if (message) {
          message.hidden = false;
          message.textContent = String(error.message || error);
        }
      })
      .finally(function () {
        self.adding = false;
        self.cta.forEach(function (button) {
          button.removeAttribute('aria-busy');
        });
        self.paint();
      });
  };

  /* A discount CODE is applied by visiting /discount/CODE, which is what the
     shopper's own link does. Nothing is applied when the store handles the
     bundle with an automatic discount or a Function instead. */
  Builder.prototype.applyDiscount = function (sums) {
    if (this.root.getAttribute('data-kbb-discount-mode') !== 'code') return Promise.resolve();

    var codes = String(this.root.getAttribute('data-kbb-codes') || '')
      .split(',')
      .map(function (pair) {
        var bits = pair.split(':');
        return {
          units: parseInt(String(bits[0]).replace(/[^0-9]/g, ''), 10),
          code: String(bits[1] || '').replace(/[^A-Za-z0-9_-]/g, '')
        };
      })
      .filter(function (entry) {
        return entry.units > 0 && entry.code;
      })
      .sort(function (a, b) {
        return a.units - b.units;
      });

    var picked = null;
    codes.forEach(function (entry) {
      if (sums.units >= entry.units) picked = entry;
    });
    if (!picked) return Promise.resolve();

    return window
      .fetch((routes().root_url || '/').replace(/\/$/, '') + '/discount/' + encodeURIComponent(picked.code), {
        method: 'GET',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      })
      .catch(function () {
        /* A refused code must never block the cart. */
      });
  };

  Builder.prototype.openCart = function () {
    doc.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } }));
    doc.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } }));
    try {
      if (typeof window.upcartToggleCart === 'function') window.upcartToggleCart();
      else if (window.UpCart && typeof window.UpCart.open === 'function') window.UpCart.open();
    } catch (e) {
      /* the drawer is the app's business, not ours */
    }
  };

  /* ------------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------------ */

  function init() {
    all(doc, '[data-kbb-root]').forEach(function (root) {
      if (root.kbbReady) return;
      root.kbbReady = true;
      new Builder(root);
    });
  }

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', init);
  else init();

  doc.addEventListener('shopify:section:load', init);
})();
